// Sagas are what field all the async requests in redux.  They make it easier
// to deal with this data, and also make it easier to test async calls from the ui
// You can read more about them here: https://github.com/redux-saga/redux-saga

import { put, call, all, takeEvery, takeLatest } from "redux-saga/effects";
import {
  getTokenRequest,
  cardControlsGetCardListRequest,
  cardControlsSessionRequest,
  getCardStatusEligibilityRequest,
  saveCardStatusRequest,
  savePinUpdateRequest,
  validatePinRequest,
  cardControlsGetMailingAddressRequest,
  cardControlsGetBranchesRequest,
  submitCardReplacementRequest,
  getCardOwners,
  getExistingTravels,
  getExemptionCountries,
  getDebitCardTravelExemptionEligibility,
  submitTravelExemption,
  cancelExistingTravel,
} from "../utils/requests";
import { hideNotification } from "../actions/notifications.actions";
import {
  setCardControlsCardList,
  setCardControlsValidationPin,
  setMailingAddress,
  setBranches,
  setTravelExemptionEligibility,
} from "../actions/cardControls.actions";
import { showModal, hideModal } from "../actions/modal.actions";
import { setToken } from "../actions/token.actions";
import { setAuthError } from "../actions/authPayment.actions";
import { setMfaRequired } from "../actions/mfa.actions";

const showLoadingModal = () => {
  return showModal("loading", "Loading...");
};

const showErrorNotification = (message) => {
  return {
    type: "SHOW_NOTIFICATION",
    notificationType: "error",
    message: message,
  };
};

export function* beginCardControlsSession({ guid, history }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const token = yield call(getTokenRequest);
    sessionStorage.setItem("_LTjwt", token);
    yield put(setToken(token));

    yield put(showLoadingModal());
    yield call(cardControlsSessionRequest, guid);

    const eligibility = yield call(getCardStatusEligibilityRequest);
      
    if (eligibility.IsEligible)
    {
      history.replace("/card-controls");
    } else {
      history.replace("/card-controls-ineligibility");
    }

    yield put(hideModal("loading"));
  } catch (error) {
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* cardControlsGetCardList() {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const cards = yield call(cardControlsGetCardListRequest);

    yield put(setCardControlsCardList(cards));
    yield put(hideModal("loading"));
  } catch (error) {
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* cardControlsSaveCardStatus(statusDetails) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());
    yield call(saveCardStatusRequest, statusDetails);

    const cards = yield call(cardControlsGetCardListRequest);
    yield put(setCardControlsCardList(cards));

    yield put(hideModal("loading"));
  } catch (error) {
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* cardControlsPinUpdate({ agreeNbr, pin, callback }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());
    const response = yield call(savePinUpdateRequest, agreeNbr, pin);

    if (callback) {
      callback(response);
    }
    yield put(hideModal("loading"));
  } catch (error) {
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* cardControlsValidatePin({ agreeNbr, pin }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const validatePin = yield call(validatePinRequest, agreeNbr, pin);
    yield put(setCardControlsValidationPin(validatePin));

    yield put(hideModal("loading"));
  } catch (error) {
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* cardControlsGetMailingList() {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const cards = yield call(cardControlsGetMailingAddressRequest);

    yield put(setMailingAddress(cards));
    yield put(hideModal("loading"));
  } catch (error) {
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* cardControlsGetBranches({ zipCode }) {
  try {
    const branches = yield call(cardControlsGetBranchesRequest, zipCode);
    yield put(setBranches(branches));
  } catch (error) {
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* cardControlsSubmitCardReplacement({ agreeNbr, cardType, callback }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());
    const response = yield call(submitCardReplacementRequest, agreeNbr, cardType);

    if (callback) {
      callback(response);
    }
    yield put(hideModal("loading"));
  } catch (error) {
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* watchBeginCardControlsSession() {
  yield takeEvery("BEGIN_CARD_CONTROLS_SESSION", beginCardControlsSession);
}

export function* watchCardControlsGetCardList() {
  yield takeEvery("CARD_CONTROLS_GET_CARD_LIST", cardControlsGetCardList);
}

export function* watchCardControlsSaveCardStatus() {
  yield takeEvery("CARD_CONTROLS_SAVE_CARD_STATUS", cardControlsSaveCardStatus);
}

export function* watchCardControlsPinUpdate() {
  yield takeEvery("CARD_CONTROLS_PIN_UPDATE", cardControlsPinUpdate);
}

export function* watchCardControlsValidatePin() {
  yield takeEvery("CARD_CONTROLS_PIN_VALIDATION", cardControlsValidatePin);
}

export function* watchCardControlsGetMailingList() {
  yield takeEvery("CARD_CONTROLS_GET_MAILING_ADDRESS", cardControlsGetMailingList);
}

export function* watchCardControlsGetBranches() {
  yield takeEvery("CARD_CONTROLS_GET_BRANCHES", cardControlsGetBranches);
}

export function* watchSubmitCardReplacement() {
  yield takeEvery("CARD_CONTROLS_SUBMIT_CARD_REPLACEMENT", cardControlsSubmitCardReplacement);
}

// Saga to get travel exemption eligibility
export function* authGetTravelExemptionEligibility({ agreeNbr, callback }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    // Mark mfa rquired to true at the beginning of eligibility requests
    yield put(setMfaRequired(true));

    const response = yield call(getDebitCardTravelExemptionEligibility, agreeNbr);

    yield put(setTravelExemptionEligibility(response));

    if (response.IsEligible) {
      yield put(setMfaRequired(!response.HasPassedMfa));

      const countryOptions = yield call(getExemptionCountries);

      yield put({
        type: "SET_COUNTRY_OPTIONS",
        countryOptions: countryOptions,
      });
    }

    if (callback) {
      callback(response);
    }

    yield put(hideModal("loading"));
  } catch (error) {
    console.log(error);

    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* watchGetTravelExemptionEligibility() {
  yield takeLatest("GET_TRAVEL_EXEMPTION_ELIGIBILITY", authGetTravelExemptionEligibility);
}

// Get card owners for travel exemption
export function* authGetCardOwners() {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    // Get card owners for the travel exemption flow - this can be moved
    // elsewhere if we ever need to make this mfa saga more generic
    const owners = yield call(getCardOwners);
    // Add some static id's, the order should remain static in the reducer
    // but if we ever need to re-order or arrange them it will be helpful
    const ownersWithIds = owners[Object.keys(owners)].map((data)=>{
      return {
        maskedCardNumber: data.MaskedCardNumber,
        cardholderName: data.CardHolderName,
        interfaceId: data.Id,
        isSelected: data.IsSelected
      };
    });

    yield put({
      type: "SET_CARD_OWNERS",
      cardOwners: ownersWithIds,
    });

    yield put(hideModal("loading"));
  } catch (error) {
    console.log(error);

    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* watchGetCardOwners() {
  yield takeLatest("GET_CARD_OWNERS", authGetCardOwners);
}

export function* authGetExistingTravels() {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());
    
    const existingTravels = yield call(getExistingTravels);

    yield put({
      type: "SET_EXISTING_TRAVELS",
      existingTravels: existingTravels,
    });

    yield put(hideModal("loading"));
  } catch (error) {
    console.log(error);

    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* cancelExistingTravelSaga({cardId, exemptionNumber}) {
    yield put(showLoadingModal());

    try {
      yield call (
        cancelExistingTravel,
        cardId,
        exemptionNumber
      );
    }
    catch (error) {
      console.log(error);
      
      yield put(setAuthError());
      yield put(hideModal("loading"));
    }
    
    yield put(hideModal("loading"));
}

export function* watchGetExistingTravels() {
  yield takeLatest("GET_EXISTING_TRAVELS", authGetExistingTravels);
}

export function* watchCancelExistingTravels() {
  yield takeLatest("CANCEL_EXISTING_TRAVEL", cancelExistingTravelSaga);
}

// Submit the travel exemption
export function* submitTravelExemptionSaga({
  exemptionDto,
  callback,
}) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const response = yield call(
      submitTravelExemption,
      exemptionDto
    );

    if (response.ErrorMessage) {
      yield put(showErrorNotification(response.ErrorMessage));
    } 
    else {
      yield put({
        type: "SET_EXEMPTION_RESPONSE",
        travelExemptionResponse: response,
      });
      
      if (callback) {
        callback();
      }
    }

    yield put(hideModal("loading"));
  } catch (error) {
    console.log(error);

    yield put(showErrorNotification("Something went wrong when submitting your travel exemption. Please try again later."));
    yield put(hideModal("loading"));
  }
}

export function* watchSubmitTravelExemption() {
  yield takeLatest("SUBMIT_TRAVEL_EXEMPTION", submitTravelExemptionSaga);
}



// WEBPACK FOOTER //
// ./src/sagas/cardControls.sagas.js